import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  collection,
  deleteField,
  doc,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  RefreshCw,
  Inbox,
  FileText,
  HelpCircle,
  Search,
  Filter,
  AlertCircle,
  ListTree,
  CreditCard,
  ChevronRight,
} from 'lucide-react';
import { db } from '../../utils/firebase';
import { useCRM } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import {
  ChangeOrder,
  Payment,
  Pipeline,
  ProjectPLBucket,
  ProposalBuilderData,
  QBExpense,
  DealPLOverrides,
} from '../../types';
import { subscribeChangeOrdersForDeal } from '../../utils/changeOrders';
import { subscribeJobPurchaseRequisitions } from '../../utils/purchasingRequisitions';
import type { PurchaseRequisition } from '../../types/purchasing';
import {
  aggregateProjectPLBatch,
  computeProjectPLGlobalCounters,
  DesignFeePaymentLite,
  PL_COLLECTED_REVENUE_NOTE,
  ProjectPLRow as ProjectPLRowData,
} from '../../utils/projectPL';
import { pickPLDeals, parseQBLinkTargetId, classifyPLDealGroup } from '../../utils/qbDealLinkOptions';
import {
  setDealPLExcluded,
  setExpenseBucketOverride,
  subscribeDealPLOverridesMap,
} from '../../utils/dealPLOverrides';
import { setPaymentDealLink, setExpenseDealLink, setExpenseAsOverhead, QB_EXPENSE_OVERHEAD_ASSIGN } from '../../utils/qbTransactionLinks';
import ProjectPLRow from '../../components/sales-activity/ProjectPLRow';
import CompletedProjectsModal from '../../components/sales-activity/CompletedProjectsModal';
import QBTransactionsModal from '../../components/sales-activity/QBTransactionsModal';
import BillingsHubModal from '../../components/finances/BillingsHubModal';
import { canReviewDesignerInvoices } from '../../utils/projectPLAccess';
import {
  subscribePendingDesignerInvoices,
  subscribeApprovedDesignerInvoices,
  formatInvoiceMoney,
  type DesignerInvoiceDoc,
} from '../../utils/designerInvoiceService';
import { flattenApprovedDesignerInvoiceJobCash, qbExpenseDedupeSets } from '../../utils/designerInvoicePL';

interface ProjectPLDashboardProps {
  qbPayments: (Payment & { id: string })[];
  qbExpenses: (QBExpense & { id: string })[];
  designFeePayments: DesignFeePaymentLite[];
  pipelines: Pipeline[];
  formatCurrency: (n: number) => string;
  formatCurrencyFull: (n: number) => string;
  syncing: boolean;
  syncResult: string | null;
  lastSyncAt: Date | null;
  onSync: () => Promise<void> | void;
  /** Hide the standalone page title when mounted inside Finances. */
  embedded?: boolean;
}

/** Compact, human-friendly relative time for the "Last synced …" hint. */
function formatRelativeTime(date: Date | null): string {
  if (!date) return 'never';
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return 'just now';
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

type RowFilter = 'all' | 'attention' | 'no-proposal' | 'ready' | 'excluded';

function buildPipelineStageNameMap(pipelines: Pipeline[]): Record<string, string> {
  const map: Record<string, string> = {};
  pipelines.forEach(p => {
    p.stages.forEach(s => {
      map[s.id] = s.name;
    });
  });
  return map;
}

const ProjectPLDashboard: React.FC<ProjectPLDashboardProps> = ({
  qbPayments,
  qbExpenses,
  designFeePayments,
  pipelines,
  formatCurrency,
  formatCurrencyFull,
  syncing,
  syncResult,
  lastSyncAt,
  onSync,
  embedded = false,
}) => {
  const { enhancedDeals, updateEnhancedDeal, timberProjects, crmUsers } = useCRM();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const me = crmUsers.find((u) => u.uid === user?.id);
  const canReviewDesignerInvoiceQueue = canReviewDesignerInvoices({
    authRole: user?.role,
    designations: me?.designations ?? user?.designations,
    briefingProfile: user?.executiveBriefing?.profile ?? me?.executiveBriefing?.profile ?? null,
  });

  const [proposalsByDeal, setProposalsByDeal] = useState<Record<string, ProposalBuilderData | null>>({});
  const [changeOrdersByDeal, setChangeOrdersByDeal] = useState<Record<string, ChangeOrder[]>>({});
  const [jobPurchases, setJobPurchases] = useState<PurchaseRequisition[]>([]);
  const [overridesByDeal, setOverridesByDeal] = useState<Record<string, DealPLOverrides | null>>({});
  const [expandedDealId, setExpandedDealId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [filter, setFilter] = useState<RowFilter>('all');
  const [search, setSearch] = useState('');
  const [underContractOpen, setUnderContractOpen] = useState(true);
  const [preconOpen, setPreconOpen] = useState(false);
  const [estimatingOpen, setEstimatingOpen] = useState(false);
  /** Deal IDs with excluded=true from a single collection query — avoids rendering every won row before per-deal override docs arrive. */
  const [excludedDealIdsQuick, setExcludedDealIdsQuick] = useState<string[]>([]);
  const [excludedQueryReady, setExcludedQueryReady] = useState(false);
  const [pendingDesignerInvoices, setPendingDesignerInvoices] = useState<DesignerInvoiceDoc[]>([]);
  const [approvedDesignerInvoices, setApprovedDesignerInvoices] = useState<DesignerInvoiceDoc[]>([]);
  const [showDesignerReview, setShowDesignerReview] = useState(false);

  useEffect(() => {
    if (!canReviewDesignerInvoiceQueue) {
      setPendingDesignerInvoices([]);
      return undefined;
    }
    return subscribePendingDesignerInvoices((list) => {
      setPendingDesignerInvoices(list);
    });
  }, [canReviewDesignerInvoiceQueue]);

  useEffect(() => {
    return subscribeApprovedDesignerInvoices((list) => {
      setApprovedDesignerInvoices(list);
    });
  }, []);

  // Skip invoices already on the P&L as their synced QuickBooks bill (qbExpenseId / qboBillId).
  const designerInvoiceJobCash = useMemo(
    () => flattenApprovedDesignerInvoiceJobCash(approvedDesignerInvoices, qbExpenseDedupeSets(qbExpenses)),
    [approvedDesignerInvoices, qbExpenses],
  );

  // Deep link from Progress Billing: /finances/project-pl?deal=<id> expands that row.
  useEffect(() => {
    const dealParam = searchParams.get('deal');
    if (!dealParam) return;
    setExpandedDealId(dealParam);
    const next = new URLSearchParams(searchParams);
    next.delete('deal');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('review') !== 'designer-invoices') return;
    if (canReviewDesignerInvoiceQueue) setShowDesignerReview(true);
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
  }, [canReviewDesignerInvoiceQueue, searchParams, setSearchParams]);

  const pendingDesignerTotal = useMemo(
    () => pendingDesignerInvoices.reduce((sum, invoice) => sum + (invoice.invoiceTotal || 0), 0),
    [pendingDesignerInvoices],
  );

  const plDeals = useMemo(
    () => pickPLDeals(enhancedDeals, timberProjects),
    [enhancedDeals, timberProjects],
  );
  const plDealIds = useMemo(() => plDeals.map(d => d.id).filter(Boolean) as string[], [plDeals]);
  const plDealIdSet = useMemo(() => new Set(plDealIds), [plDealIds]);

  /** Fast path: which projects are excluded before the per-deal override snapshots finish streaming. */
  useEffect(() => {
    if (plDealIds.length === 0) {
      setExcludedDealIdsQuick([]);
      setExcludedQueryReady(true);
      return;
    }
    setExcludedQueryReady(false);
    const q = query(collection(db, 'dealPLOverrides'), where('excluded', '==', true));
    const unsub = onSnapshot(
      q,
      snap => {
        const next: string[] = [];
        snap.forEach(docSnap => {
          const id = docSnap.id;
          if (plDealIdSet.has(id)) next.push(id);
        });
        setExcludedDealIdsQuick(next);
        setExcludedQueryReady(true);
      },
      err => {
        console.error('dealPLOverrides excluded query failed:', err);
        setExcludedDealIdsQuick([]);
        setExcludedQueryReady(true);
      },
    );
    return () => unsub();
  }, [plDealIds, plDealIdSet]);

  const excludedQuickSet = useMemo(() => new Set(excludedDealIdsQuick), [excludedDealIdsQuick]);

  const overridesForPL = useMemo(() => {
    const o: Record<string, DealPLOverrides | null> = { ...overridesByDeal };
    excludedQuickSet.forEach(id => {
      const prev = o[id];
      // Per-deal snapshot already cleared the flag — don't let the collection
      // query pin the row excluded for an extra tick after Re-include.
      if (prev?.excluded === false) return;
      o[id] = prev
        ? { ...prev, dealId: id, excluded: true }
        : { dealId: id, excluded: true, expenseBuckets: {} };
    });
    return o;
  }, [overridesByDeal, excludedQuickSet]);

  const proposalDealIds = useMemo(() => {
    if (!excludedQueryReady) return [] as string[];
    if (filter === 'excluded') {
      return plDealIds.filter(id => excludedQuickSet.has(id));
    }
    return plDealIds.filter(id => !excludedQuickSet.has(id));
  }, [plDealIds, excludedQuickSet, excludedQueryReady, filter]);

  const proposalDealIdsKey = useMemo(() => [...proposalDealIds].sort().join('|'), [proposalDealIds]);

  useEffect(() => {
    const ids = new Set(proposalDealIds);
    const unsubs: Array<() => void> = [];
    setProposalsByDeal(prev => {
      const next: Record<string, ProposalBuilderData | null> = {};
      Object.entries(prev).forEach(([id, val]) => {
        if (ids.has(id)) next[id] = val;
      });
      return next;
    });
    proposalDealIds.forEach(id => {
      unsubs.push(
        onSnapshot(
          doc(db, 'proposalBuilders', id),
          snap => {
            setProposalsByDeal(prev => ({
              ...prev,
              [id]: snap.exists() ? ({ id: snap.id, ...(snap.data() as any) } as ProposalBuilderData) : null,
            }));
          },
          () => {
            setProposalsByDeal(prev => ({ ...prev, [id]: null }));
          },
        ),
      );
    });
    return () => {
      unsubs.forEach(u => {
        try {
          u();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalDealIdsKey]);

  useEffect(() => {
    const ids = new Set(proposalDealIds);
    const unsubs: Array<() => void> = [];
    setChangeOrdersByDeal(prev => {
      const next: Record<string, ChangeOrder[]> = {};
      Object.entries(prev).forEach(([id, val]) => {
        if (ids.has(id)) next[id] = val;
      });
      return next;
    });
    proposalDealIds.forEach(id => {
      unsubs.push(
        subscribeChangeOrdersForDeal(id, orders => {
          setChangeOrdersByDeal(prev => ({ ...prev, [id]: orders }));
        }),
      );
    });
    return () => {
      unsubs.forEach(u => {
        try {
          u();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalDealIdsKey]);

  useEffect(() => {
    const unsub = subscribeDealPLOverridesMap(plDealIds, map => setOverridesByDeal(map));
    return () => unsub();
  }, [plDealIds]);

  useEffect(() => subscribeJobPurchaseRequisitions(setJobPurchases), []);

  const stageNameMap = useMemo(() => buildPipelineStageNameMap(pipelines), [pipelines]);

  const pipelineStageNamesWithPrecon = useMemo(() => {
    const names = { ...stageNameMap };
    plDeals.forEach(d => {
      if (!d.id || d.outcome === 'won') return;
      const group = classifyPLDealGroup(d, timberProjects);
      if (group === 'preconstruction') names[d.stageId] = 'Preconstruction';
      if (group === 'estimating') names[d.stageId] = 'Estimating';
    });
    return names;
  }, [stageNameMap, timberProjects, plDeals]);

  const allRows = useMemo<ProjectPLRowData[]>(
    () => {
      if (!excludedQueryReady) return [];
      return aggregateProjectPLBatch({
        deals: plDeals,
        proposalsByDeal,
        changeOrdersByDeal,
        jobPurchases,
        designerInvoiceJobCash,
        expenses: qbExpenses,
        payments: qbPayments,
        designFeePayments,
        overridesByDeal: overridesForPL,
        pipelineStageNames: pipelineStageNamesWithPrecon,
      });
    },
    [
      excludedQueryReady,
      plDeals,
      proposalsByDeal,
      changeOrdersByDeal,
      jobPurchases,
      designerInvoiceJobCash,
      qbExpenses,
      qbPayments,
      designFeePayments,
      overridesForPL,
      pipelineStageNamesWithPrecon,
    ],
  );

  const activeRows = useMemo(
    () => allRows.filter(r => !r.pmFinalizedAt).sort((a, b) => b.revenue - a.revenue),
    [allRows],
  );
  const completedRows = useMemo(
    () =>
      allRows
        .filter(r => r.pmFinalizedAt)
        .sort((a, b) => (b.pmFinalizedAt?.getTime() || 0) - (a.pmFinalizedAt?.getTime() || 0)),
    [allRows],
  );

  const excludedRows = useMemo(
    () => allRows.filter(r => r.excluded).sort((a, b) => b.revenue - a.revenue),
    [allRows],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = filter === 'excluded' ? excludedRows : activeRows;
    return pool.filter(r => {
      if (q && !r.dealTitle.toLowerCase().includes(q) && !r.contactName.toLowerCase().includes(q)) {
        return false;
      }
      switch (filter) {
        case 'attention':
          return !r.excluded && r.unassignedExpenseCount > 0;
        case 'no-proposal':
          return !r.excluded && !r.hasProposal;
        case 'ready':
          return !r.excluded && r.readyToFinalize;
        case 'excluded':
          return true;
        default:
          if (q && r.excluded) return true;
          return !r.excluded;
      }
    });
  }, [activeRows, excludedRows, filter, search]);

  const groupByDealId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof classifyPLDealGroup>>();
    for (const deal of plDeals) {
      if (!deal.id) continue;
      map.set(deal.id, classifyPLDealGroup(deal, timberProjects));
    }
    return map;
  }, [plDeals, timberProjects]);

  const underContractRows = useMemo(
    () => filteredRows.filter(r => (groupByDealId.get(r.dealId) ?? 'contracted') === 'contracted'),
    [filteredRows, groupByDealId],
  );
  const preconRows = useMemo(
    () => filteredRows.filter(r => groupByDealId.get(r.dealId) === 'preconstruction'),
    [filteredRows, groupByDealId],
  );
  const estimatingRows = useMemo(
    () => filteredRows.filter(r => groupByDealId.get(r.dealId) === 'estimating'),
    [filteredRows, groupByDealId],
  );

  /** With an active search, force all groups open so matches are never hidden. */
  const searching = search.trim().length > 0;

  const globalCounters = useMemo(
    () => computeProjectPLGlobalCounters(qbExpenses, qbPayments),
    [qbExpenses, qbPayments],
  );

  const filterCounts = useMemo(
    () => ({
      all: activeRows.filter(r => !r.excluded).length,
      attention: activeRows.filter(r => !r.excluded && r.unassignedExpenseCount > 0).length,
      noProposal: activeRows.filter(r => !r.excluded && !r.hasProposal).length,
      ready: activeRows.filter(r => !r.excluded && r.readyToFinalize).length,
      excluded: excludedRows.length,
    }),
    [activeRows, excludedRows],
  );

  const handleToggleExpand = useCallback((dealId: string) => {
    setExpandedDealId(prev => (prev === dealId ? null : dealId));
  }, []);

  const handleExpenseBucketChange = useCallback(
    async (dealId: string, expenseId: string, bucket: ProjectPLBucket | null) => {
      try {
        await setExpenseBucketOverride(dealId, expenseId, bucket, { uid: user?.id });
      } catch (err) {
        console.error('Error saving expense bucket override:', err);
      }
    },
    [user?.id],
  );

  const handleFinalize = useCallback(
    async (dealId: string) => {
      try {
        await updateEnhancedDeal(dealId, {
          pmFinalizedAt: new Date(),
          pmFinalizedBy: user?.id || 'unknown',
        });
      } catch (err) {
        console.error('Error finalizing project:', err);
      }
    },
    [updateEnhancedDeal, user?.id],
  );

  const handleToggleExcluded = useCallback(
    async (dealId: string, excluded: boolean, reason?: string) => {
      try {
        await setDealPLExcluded(dealId, excluded, { uid: user?.id, reason });
      } catch (err) {
        console.error('Error updating exclusion flag:', err);
      }
    },
    [user?.id],
  );

  const handleReassignPayment = useCallback(
    async (paymentId: string, dealId: string | null) => {
      const dealTitle = dealId ? plDeals.find(d => d.id === dealId)?.title : undefined;
      try {
        await setPaymentDealLink(paymentId, dealId, { uid: user?.id, dealTitle });
      } catch (err) {
        console.error('Error reassigning payment:', err);
      }
    },
    [user?.id, plDeals],
  );

  const handleReassignExpense = useCallback(
    async (expenseId: string, target: string) => {
      try {
        if (target === QB_EXPENSE_OVERHEAD_ASSIGN) {
          await setExpenseAsOverhead(expenseId, { uid: user?.id });
          return;
        }
        const parsed = parseQBLinkTargetId(target || null);
        const dealId = parsed.dealId;
        const dealTitle = dealId ? plDeals.find(d => d.id === dealId)?.title : undefined;
        await setExpenseDealLink(expenseId, dealId, { uid: user?.id, dealTitle });
      } catch (err) {
        console.error('Error reassigning expense:', err);
      }
    },
    [user?.id, plDeals],
  );

  const handleReopen = useCallback(async (dealId: string) => {
    try {
      await updateDoc(doc(db, 'enhancedDeals', dealId), {
        pmFinalizedAt: deleteField(),
        pmFinalizedBy: deleteField(),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error reopening project:', err);
    }
  }, []);

  const renderRow = useCallback(
    (row: ProjectPLRowData) => (
      <ProjectPLRow
        key={row.dealId}
        row={row}
        expanded={expandedDealId === row.dealId}
        onToggle={() => handleToggleExpand(row.dealId)}
        onExpenseBucketChange={(expenseId, bucket) =>
          handleExpenseBucketChange(row.dealId, expenseId, bucket)
        }
        onFinalize={() => handleFinalize(row.dealId)}
        onToggleExcluded={(excluded, reason) =>
          handleToggleExcluded(row.dealId, excluded, reason)
        }
        onReassignPayment={handleReassignPayment}
        onReassignExpense={handleReassignExpense}
        reassignableDeals={plDeals}
        formatCurrency={formatCurrency}
        formatCurrencyFull={formatCurrencyFull}
        onReopen={row.pmFinalizedAt ? () => handleReopen(row.dealId) : undefined}
      />
    ),
    [
      expandedDealId,
      handleToggleExpand,
      handleExpenseBucketChange,
      handleFinalize,
      handleToggleExcluded,
      handleReassignPayment,
      handleReassignExpense,
      plDeals,
      formatCurrency,
      formatCurrencyFull,
      handleReopen,
    ],
  );

  return (
    <div className={`ppl${embedded ? ' ppl--embedded' : ''}`}>
      <header className="ppl-header">
        {!embedded && (
          <div className="ppl-header-titlewrap">
            <h1 className="ppl-header-title">Project P&amp;L</h1>
            <p className="ppl-header-sub">
              Live revenue, cost, and budget variance for under-contract, preconstruction, and
              estimating projects. Tap a row to drill in, recategorize an expense, or finalize
              once the books are closed.
            </p>
          </div>
        )}
        <div className="ppl-header-tools">
          <button
            type="button"
            className="ppl-help-toggle"
            onClick={() => setShowHelp(s => !s)}
            aria-pressed={showHelp}
            title="What's in this view?"
          >
            <HelpCircle size={14} />
            <span>How it works</span>
          </button>
          <div className="ppl-sync-group">
            <button
              type="button"
              className="ppl-btn ppl-btn-ghost"
              onClick={() => onSync()}
              disabled={syncing}
              title={
                lastSyncAt
                  ? `Auto-syncs daily. Last synced ${lastSyncAt.toLocaleString()}.`
                  : 'Pull the latest QuickBooks invoices, payments, and expenses. Auto-syncs daily.'
              }
            >
              <RefreshCw size={13} className={syncing ? 'spinning' : ''} />
              {syncing ? 'Syncing' : 'Sync QB'}
            </button>
            <span
              className="ppl-last-synced"
              title={lastSyncAt ? lastSyncAt.toLocaleString() : 'Daily auto-sync hasn\u2019t run yet'}
            >
              {syncing
                ? 'Syncing now\u2026'
                : lastSyncAt
                ? `Last synced ${formatRelativeTime(lastSyncAt)}`
                : 'Auto-syncs daily'}
            </span>
          </div>
          <button
            type="button"
            className="ppl-btn ppl-btn-ghost"
            onClick={() => setShowTransactions(true)}
            title="Browse every QuickBooks payment and expense, and reassign their linked project."
          >
            <ListTree size={13} />
            QB transactions
            {globalCounters.unmatchedTxnCount > 0 && (
              <span className="ppl-btn-badge ppl-btn-badge-warn">{globalCounters.unmatchedTxnCount}</span>
            )}
          </button>
          {user?.role === 'admin' && (
            <Link
              to="/settings/integrations#sequence-cards"
              className="ppl-btn ppl-btn-ghost"
              title="Map company debit cards to CRM users."
            >
              <CreditCard size={13} />
              Card registry
            </Link>
          )}
          <button
            type="button"
            className="ppl-btn ppl-btn-primary"
            onClick={() => setShowCompleted(true)}
            title="View finalized projects."
          >
            <Inbox size={13} />
            Completed
            <span className="ppl-btn-badge">{completedRows.length}</span>
          </button>
        </div>
      </header>

      {showHelp && (
        <section className="ppl-help">
          <div className="ppl-help-grid">
            <div>
              <div className="ppl-help-label">Revenue</div>
              <p>
                Signed deal value plus any Stripe design fees collected for that project. Jobs that predate the CRM
                (no proposal, no deal value) show cash collected instead, marked <strong>*</strong>.
              </p>
            </div>
            <div>
              <div className="ppl-help-label">Actual cost</div>
              <p>QuickBooks expenses matched to the deal, plus a revenue-share allocation of company overhead.</p>
            </div>
            <div>
              <div className="ppl-help-label">Budget</div>
              <p>Pulled from the latest proposal — raw cost (pre-margin) per bucket so variance is apples-to-apples.</p>
            </div>
            <div>
              <div className="ppl-help-label">Finalize</div>
              <p>Once you've reviewed all expenses, click Finalize to archive the project. Reopen anytime from the archive.</p>
            </div>
          </div>
        </section>
      )}

      {syncResult && (
        <div className="ppl-banner ppl-banner-info">{syncResult}</div>
      )}

      {globalCounters.pendingCardExpenseCount > 0 && (
        <div className="ppl-banner ppl-banner-warn">
          <AlertCircle size={14} />
          <span>
            <strong>{globalCounters.pendingCardExpenseCount}</strong> company card charge
            {globalCounters.pendingCardExpenseCount === 1 ? '' : 's'} ({formatCurrency(globalCounters.pendingCardExpenseAmount)})
            {' '}awaiting cardholder categorization — excluded from project totals until assigned.
            {user?.role === 'admin' && (
              <>
                {' '}
                <Link to="/settings/integrations#sequence-cards">Manage card registry</Link>
              </>
            )}
          </span>
        </div>
      )}

      {globalCounters.unmatchedTxnCount > 0 && (
        <div className="ppl-banner ppl-banner-warn">
          <AlertCircle size={14} />
          <span>
            <strong>{globalCounters.unmatchedTxnCount}</strong> QuickBooks transaction
            {globalCounters.unmatchedTxnCount === 1 ? '' : 's'}
            {' '}({formatCurrency(globalCounters.unmatchedExpenseAmount + globalCounters.unmatchedPaymentAmount)})
            {' '}aren't matched to a project yet
            {globalCounters.unmatchedPaymentCount > 0
              ? ` — ${globalCounters.unmatchedExpenseCount} expense${globalCounters.unmatchedExpenseCount === 1 ? '' : 's'} and ${globalCounters.unmatchedPaymentCount} payment${globalCounters.unmatchedPaymentCount === 1 ? '' : 's'}`
              : ''}
            . Open <em>QB transactions</em> to assign them, or set the customer in QuickBooks
            so the next daily sync picks them up automatically.
          </span>
        </div>
      )}

      {canReviewDesignerInvoiceQueue && pendingDesignerInvoices.length > 0 && (
        <div className="ppl-banner ppl-banner-action" role="status">
          <FileText size={14} />
          <span>
            <strong>{pendingDesignerInvoices.length}</strong> bill
            {pendingDesignerInvoices.length === 1 ? '' : 's'}{' '}
            ({formatInvoiceMoney(pendingDesignerTotal)}) in the Billings Hub.
          </span>
          <button
            type="button"
            className="ppl-btn ppl-btn-primary ppl-banner-btn"
            onClick={() => setShowDesignerReview(true)}
          >
            Open hub
          </button>
        </div>
      )}

      <section className="ppl-toolbar">
        <div className="ppl-search">
          <Search size={14} />
          <input
            placeholder="Search project or client…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search projects"
          />
        </div>
        <div className="ppl-filters" role="tablist" aria-label="Project filter">
          <FilterPill
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            count={excludedQueryReady ? filterCounts.all : null}
            icon={<Filter size={11} />}
            tooltip="All active (won, not-yet-finalized) projects, excluding any you've manually hidden."
          >
            All
          </FilterPill>
          <FilterPill
            active={filter === 'attention'}
            onClick={() => setFilter('attention')}
            count={excludedQueryReady ? filterCounts.attention : null}
            tone="warn"
            tooltip="Projects with QuickBooks expenses that haven't been categorized into a proposal bucket yet. Open the row to assign each expense."
          >
            Needs sorting
          </FilterPill>
          <FilterPill
            active={filter === 'no-proposal'}
            onClick={() => setFilter('no-proposal')}
            count={excludedQueryReady ? filterCounts.noProposal : null}
            tone="muted"
            tooltip="Won projects with no proposal builder attached, so there's no budget to compare actuals against. Build or link a proposal to see variance."
          >
            No proposal
          </FilterPill>
          <FilterPill
            active={filter === 'ready'}
            onClick={() => setFilter('ready')}
            count={excludedQueryReady ? filterCounts.ready : null}
            tone="ok"
            tooltip="Projects where every expense is categorized and there's nothing left to sort — safe to mark as finalized."
          >
            Ready
          </FilterPill>
          {excludedQueryReady && (filterCounts.excluded > 0 || filter === 'excluded') && (
            <FilterPill
              active={filter === 'excluded'}
              onClick={() => setFilter('excluded')}
              count={filterCounts.excluded}
              tone="muted"
              tooltip="Projects you've hidden from KPI rollups and overhead allocation. Open the list and click Re-include to put one back."
            >
              Excluded
            </FilterPill>
          )}
        </div>
      </section>

      <section
        className="ppl-table"
        aria-label="Active projects"
        aria-busy={!excludedQueryReady}
      >
        <div className="ppl-table-head">
          <span aria-hidden />
          <span>Project</span>
          <span className="ppl-num">Revenue</span>
          <span className="ppl-num">Actual cost</span>
          <span className="ppl-num">Budget</span>
          <span className="ppl-num">Margin</span>
          <span className="ppl-num">vs Budget</span>
          <span className="ppl-status">Status</span>
        </div>
        {!excludedQueryReady ? (
          <div className="ppl-empty">Loading projects…</div>
        ) : filteredRows.length === 0 ? (
          <div className="ppl-empty">
            {activeRows.length === 0
              ? 'No active projects yet. Won, preconstruction, and estimating deals will appear here automatically.'
              : 'No projects match this filter.'}
          </div>
        ) : (
          <>
            {underContractRows.length > 0 && (
              <>
                <PLGroupHeader
                  label="Under Contract"
                  count={underContractRows.length}
                  totalRevenue={underContractRows.reduce((sum, r) => sum + r.revenue, 0)}
                  open={underContractOpen || searching}
                  onToggle={() => setUnderContractOpen(o => !o)}
                  formatCurrencyFull={formatCurrencyFull}
                />
                {(underContractOpen || searching) && underContractRows.map(renderRow)}
              </>
            )}
            {preconRows.length > 0 && (
              <>
                <PLGroupHeader
                  label="Preconstruction"
                  count={preconRows.length}
                  totalRevenue={preconRows.reduce((sum, r) => sum + r.revenue, 0)}
                  open={preconOpen || searching}
                  onToggle={() => setPreconOpen(o => !o)}
                  formatCurrencyFull={formatCurrencyFull}
                />
                {(preconOpen || searching) && preconRows.map(renderRow)}
              </>
            )}
            {estimatingRows.length > 0 && (
              <>
                <PLGroupHeader
                  label="Estimating"
                  count={estimatingRows.length}
                  totalRevenue={estimatingRows.reduce((sum, r) => sum + r.revenue, 0)}
                  open={estimatingOpen || searching}
                  onToggle={() => setEstimatingOpen(o => !o)}
                  formatCurrencyFull={formatCurrencyFull}
                />
                {(estimatingOpen || searching) && estimatingRows.map(renderRow)}
              </>
            )}
            {filteredRows.some(r => r.revenueSource === 'collected') && (
              <div className="ppl-footnote">{PL_COLLECTED_REVENUE_NOTE}</div>
            )}
          </>
        )}
      </section>

      <CompletedProjectsModal
        open={showCompleted}
        rows={completedRows}
        formatCurrencyFull={formatCurrencyFull}
        onClose={() => setShowCompleted(false)}
        onReopen={handleReopen}
        onReinclude={(dealId) => handleToggleExcluded(dealId, false)}
      />

      <QBTransactionsModal
        open={showTransactions}
        qbPayments={qbPayments}
        qbExpenses={qbExpenses}
        deals={plDeals}
        formatCurrencyFull={formatCurrencyFull}
        onClose={() => setShowTransactions(false)}
        uid={user?.id}
      />

      {canReviewDesignerInvoiceQueue && (
        <BillingsHubModal
          open={showDesignerReview}
          onClose={() => setShowDesignerReview(false)}
        />
      )}
    </div>
  );
};

interface PLGroupHeaderProps {
  label: string;
  count: number;
  totalRevenue: number;
  open: boolean;
  onToggle: () => void;
  formatCurrencyFull: (n: number) => string;
}

const PLGroupHeader: React.FC<PLGroupHeaderProps> = ({
  label,
  count,
  totalRevenue,
  open,
  onToggle,
  formatCurrencyFull,
}) => (
  <button
    type="button"
    className={`ppl-group-head ${open ? 'open' : ''}`}
    onClick={onToggle}
    aria-expanded={open}
    title={open ? `Collapse ${label}` : `Expand ${label}`}
  >
    <ChevronRight size={14} className="ppl-group-chevron" />
    <span className="ppl-group-label">{label}</span>
    <span className="ppl-group-count">{count}</span>
    <span className="ppl-group-total">{formatCurrencyFull(totalRevenue)} revenue</span>
  </button>
);

interface FilterPillProps {
  active: boolean;
  onClick: () => void;
  count: number | null;
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: 'warn' | 'ok' | 'muted';
  tooltip?: string;
}

const FilterPill: React.FC<FilterPillProps> = ({ active, onClick, count, children, icon, tone, tooltip }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    aria-label={tooltip}
    title={tooltip}
    className={`ppl-filter ${active ? 'active' : ''} ${tone ? `ppl-filter-${tone}` : ''}`}
    onClick={onClick}
  >
    {icon && <span className="ppl-filter-icon">{icon}</span>}
    <span>{children}</span>
    <span className="ppl-filter-count">{count == null ? '–' : count}</span>
  </button>
);

export default ProjectPLDashboard;
